import type { KeyboardEvent, PropsWithChildren } from "react";
import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import classes from "./slider.module.css";

interface SliderProps {}
interface SliderMarkersProps {
  slideCount: number;
  activeSlideIndex: number;
  slideIdPrefix: string;
  clickHandler: (slideIndex: number) => void;
}
interface SlideProps {
  isActive?: boolean;
  slideCount?: number;
  slideId?: string;
  slideIndex?: number;
}

const Slide = ({
  children,
  isActive = true,
  slideCount = 1,
  slideId,
  slideIndex = 0,
}: PropsWithChildren<SlideProps>) => (
  <div
    id={slideId}
    className={classes.marketing__slider_slide}
    role="group"
    aria-roledescription="slide"
    aria-label={`Slide ${slideIndex + 1} of ${slideCount}`}
    aria-hidden={isActive ? undefined : true}
  >
    {children}
  </div>
);

const SliderMarkers = ({
  slideCount,
  activeSlideIndex,
  slideIdPrefix,
  clickHandler,
}: SliderMarkersProps) => (
  <ul className={classes.marketing__slider_markers}>
    {new Array(slideCount).fill(0).map((_, i) => {
      const markerClasses = [classes.marketing__slider_markers_marker];
      if (i === activeSlideIndex)
        markerClasses.push(classes.marketing__slider_markers_marker_active);

      return (
        <li
          key={`slide_${i}`}
          className={markerClasses.join(" ")}
          role="button"
          tabIndex={0}
          aria-label={`Show slide ${i + 1} of ${slideCount}`}
          aria-pressed={i === activeSlideIndex}
          aria-controls={`${slideIdPrefix}-slide-${i + 1}`}
          onClick={() => clickHandler(i)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              clickHandler(i);
            }
          }}
        ></li>
      );
    })}
  </ul>
);

const Slider = ({ children }: PropsWithChildren<SliderProps>) => {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const sliderContainerRef = useRef<HTMLDivElement>(null);
  const sliderId = useId();
  const slideCount = Children.count(children);

  const nextSlide = () =>
    setActiveSlideIndex((current) =>
      slideCount ? (current + 1) % slideCount : 0,
    );
  const prevSlide = () =>
    setActiveSlideIndex((current) =>
      slideCount ? (current - 1 + slideCount) % slideCount : 0,
    );
  const toSlide = (slideIndex: number) => {
    if (slideIndex >= 0 && slideIndex < slideCount)
      setActiveSlideIndex(slideIndex);
  };

  const labelledSlides = Children.map(children, (child, slideIndex) => {
    if (!isValidElement<SlideProps>(child) || child.type !== Slide)
      return child;

    return cloneElement(child, {
      isActive: slideIndex === activeSlideIndex,
      slideCount,
      slideId: `${sliderId}-slide-${slideIndex + 1}`,
      slideIndex,
    });
  });

  useEffect(() => {
    const firstSlide =
      sliderContainerRef.current?.querySelector<HTMLDivElement>(
        `.${classes.marketing__slider_slide}:first-of-type`,
      );
    if (firstSlide)
      firstSlide.style.marginLeft = `-${activeSlideIndex * 100}vw`;
  }, [activeSlideIndex]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      prevSlide();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      nextSlide();
    } else if (event.key === "Home") {
      event.preventDefault();
      toSlide(0);
    } else if (event.key === "End") {
      event.preventDefault();
      toSlide(slideCount - 1);
    }
  };

  return (
    <div
      className={classes.marketing__slider}
      ref={sliderContainerRef}
      role="region"
      aria-roledescription="carousel"
      aria-label="Featured content"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {labelledSlides}
      <SliderMarkers
        slideCount={slideCount}
        activeSlideIndex={activeSlideIndex}
        slideIdPrefix={sliderId}
        clickHandler={toSlide}
      />
      <svg
        role="button"
        tabIndex={0}
        aria-label="Show previous slide"
        className={`${classes.marketing__slider_arrow} ${classes.marketing__slider_arrow_left}`}
        onClick={prevSlide}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            prevSlide();
          }
        }}
        xmlns="http://www.w3.org/2000/svg"
        width="23.182"
        height="42.121"
      >
        <path
          d="M22.121 1.062l-20 20 20 20"
          fill="none"
          stroke="#00ab9d"
          strokeWidth="3"
        />
      </svg>
      <svg
        role="button"
        tabIndex={0}
        aria-label="Show next slide"
        className={`${classes.marketing__slider_arrow} ${classes.marketing__slider_arrow_right}`}
        onClick={nextSlide}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            nextSlide();
          }
        }}
        xmlns="http://www.w3.org/2000/svg"
        width="23.182"
        height="42.121"
      >
        <path
          d="M1.06 41.059l20-20-20-20"
          fill="none"
          stroke="#00ab9d"
          strokeWidth="3"
        />
      </svg>
    </div>
  );
};

export { Slide, Slider };
